/**
 * SuperMemo SM-2 Spaced Repetition System (SRS) Engine
 * Designed for German vocabulary learning & retention.
 */

const STORAGE_KEY = 'deutsch_srs_deck_v1';

const getStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  return window.localStorage;
};

export const SRS_GRADES = {
  AGAIN: 1, // Forgot completely -> reset
  HARD: 2,  // Remembered with great difficulty
  GOOD: 3,  // Remembered with standard effort
  EASY: 4,  // Remembered instantly without effort
};

export const GRADE_LABELS = {
  [SRS_GRADES.AGAIN]: { label: 'Përsërit', time: '10 min', color: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
  [SRS_GRADES.HARD]:  { label: 'Vështirë', time: '1 ditë', color: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' },
  [SRS_GRADES.GOOD]:  { label: 'Mirë', time: '3 ditë', color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
  [SRS_GRADES.EASY]:  { label: 'Lehtë', time: '6 ditë', color: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
};

export const getStoredSrsDeck = () => {
  const storage = getStorage();
  if (!storage) {
    return {};
  }

  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    console.error('Failed to load SRS deck from localStorage:', e);
    return {};
  }
};

export const saveSrsDeck = (deck) => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(deck));
  } catch (e) {
    console.error('Failed to save SRS deck:', e);
  }
};

/**
 * Core SM-2 Calculation
 * @param {Object} currentData { interval, repetition, easeFactor }
 * @param {number} grade 1 (Again), 2 (Hard), 3 (Good), 4 (Easy)
 * @returns {Object} { interval, repetition, easeFactor, dueDate }
 */
export const calculateSm2 = (currentData = {}, grade) => {
  let interval = currentData.interval || 0;
  let repetition = currentData.repetition || 0;
  let easeFactor = currentData.easeFactor || 2.5;

  // Grade mapping to SM-2 quality (0 to 5)
  // 1: Again (1), 2: Hard (3), 3: Good (4), 4: Easy (5)
  const quality = grade === 1 ? 1 : grade === 2 ? 3 : grade === 3 ? 4 : 5;

  // Update Ease Factor
  easeFactor = easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
  if (easeFactor < 1.3) easeFactor = 1.3;

  if (quality < 3) {
    // Failed recall -> start over
    repetition = 0;
    interval = 1;
  } else {
    // Successful recall
    if (repetition === 0) {
      interval = 1;
    } else if (repetition === 1) {
      interval = grade === SRS_GRADES.HARD ? 2 : 4;
    } else {
      const bonus = grade === SRS_GRADES.EASY ? 1.3 : 1.0;
      interval = Math.round(interval * easeFactor * bonus);
    }
    repetition += 1;
  }

  const now = new Date();
  const due = new Date();
  if (grade === SRS_GRADES.AGAIN) {
    // Due today in 10 minutes
    due.setMinutes(now.getMinutes() + 10);
  } else {
    due.setDate(now.getDate() + interval);
  }

  return {
    interval,
    repetition,
    easeFactor: Number(easeFactor.toFixed(2)),
    dueDate: due.toISOString(),
    lastReviewed: now.toISOString(),
  };
};

/**
 * Record a review for a specific German word
 */
export const reviewWord = (word, grade) => {
  const deck = getStoredSrsDeck();
  const existing = deck[word] || {
    interval: 0,
    repetition: 0,
    easeFactor: 2.5,
  };

  const updated = calculateSm2(existing, grade);
  deck[word] = updated;
  saveSrsDeck(deck);

  return updated;
};

/**
 * Filter a list of vocabulary entries to only those currently due for review
 */
export const getDueEntries = (allEntries = []) => {
  const deck = getStoredSrsDeck();
  const now = new Date().getTime();

  return allEntries.filter((entry) => {
    const srs = deck[entry.de];
    if (!srs) return true; // New unreviewed card
    return new Date(srs.dueDate).getTime() <= now;
  });
};

/**
 * Get summary stats for the dashboard
 */
export const getSrsStats = (totalCatalogWords = 0) => {
  const deck = getStoredSrsDeck();
  const now = new Date().getTime();

  let dueCount = 0;
  let learnedCount = 0;
  let masteredCount = 0;

  Object.values(deck).forEach((card) => {
    if (new Date(card.dueDate).getTime() <= now) {
      dueCount += 1;
    }
    if (card.repetition > 0) {
      learnedCount += 1;
    }
    if (card.repetition >= 4 || card.interval >= 14) {
      masteredCount += 1;
    }
  });

  return {
    dueCount,
    learnedCount,
    masteredCount,
    totalTracked: Object.keys(deck).length,
  };
};
